import React, { useState } from 'react';
import { ArrowRight, Trees } from 'lucide-react';
import ContactForm from '../components/ContactForm';
import treeFelling1 from '../images/Tree felling 1.jpeg';
import treeFelling4 from '../images/tree felling 4.jpeg';
import treeFelling5 from '../images/tree felling 5.jpeg';
import treeFelling6 from '../images/tree felling 6.jpeg';
import treeFelling8 from '../images/tree felling 8.jpeg';
import treeFelling9 from '../images/tree felling 9.jpeg';
import treeFelling12 from '../images/tree felling 12.jpeg';
import treeFelling14 from '../images/tree felling 14.jpeg';

// ── Tree Felling Theme Tokens ──────────────────────────────
const TF = {
  bgPrimary:    '#0a1a0a',   // deep forest black-green
  bgCard:       '#122412',   // dark forest green card
  bgCardAlt:    '#1a2e1a',   // slightly lighter card
  green:        '#2d6a2d',   // forest green primary
  greenLight:   '#4ade80',   // bright green for icons/checkmarks
  orange:       '#f97316',   // safety orange accent
  textHeading:  '#f0f5f0',   // near white
  textBody:     '#b8d4b8',   // muted green-white
  textMuted:    '#7a9e7a',   // subdued green
  textDanger:   '#f87171',   // red for disadvantages
  borderGreen:  '#2d4a2d',
};

const tfStyles = {
  page:         { backgroundColor: TF.bgPrimary, fontFamily: "'Montserrat', sans-serif", color: TF.textBody },
  h1:           { fontFamily: "'Playfair Display', serif", color: TF.textHeading, fontSize: '3rem', lineHeight: 1.1, margin: '0 0 1rem' },
  h2:           { fontFamily: "'Playfair Display', serif", color: TF.textHeading, fontSize: '2.2rem', margin: '0 0 1rem' },
  h4:           { fontFamily: "'Playfair Display', serif", color: TF.textHeading, fontSize: '1rem', margin: '0 0 0.5rem' },
  overline:     { fontSize: '0.8rem', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.18em', color: TF.orange, marginBottom: '0.75rem', fontFamily: "'Montserrat', sans-serif" },
  accentLine:   { width: '40px', height: '3px', background: TF.orange, margin: '0.75rem auto 0' },
  section:      { padding: '5rem 1.5rem', backgroundColor: TF.bgPrimary },
  sectionAlt:   { padding: '5rem 1.5rem', backgroundColor: '#0d1f0d' },
  card:         { backgroundColor: TF.bgCard, border: `0.5px solid ${TF.borderGreen}`, borderRadius: '10px', padding: '1.25rem' },
  btnPrimary:   { background: TF.orange, color: TF.bgPrimary, border: 'none', borderRadius: '6px', padding: '12px 28px', fontFamily: "'Montserrat', sans-serif", fontWeight: '600', fontSize: '0.95rem', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' },
  advantageBox: { flex: '1 1 calc(50% - 0.5rem)', backgroundColor: '#0f2010', border: `0.5px solid ${TF.borderGreen}`, borderRadius: '8px', padding: '0.75rem 1rem' },
  disadvantageBox: { flex: '1 1 calc(50% - 0.5rem)', backgroundColor: '#2a0f0f', border: '0.5px solid #4a2020', borderRadius: '8px', padding: '0.75rem 1rem' },
};

const services = [
  'Large Tree Removal','Stump Grinding & Removal','Emergency Tree Removal',
  'Tree Trimming & Pruning','Dead Tree Removal','Storm Damage Cleanup',
  'Branch Removal','Tree Risk Assessment','Land Clearing',
  'Hazardous Tree Removal','Root Removal & Treatment'
];

const galleryItems = [
  { img: treeFelling4, title: 'Unwanted Tree Removal', desc: 'Removing trees that are out of place, overgrown, or no longer wanted on the property.', adv: 'Frees up space, improves curb appeal, eliminates root damage risks to paving or foundations.', dis: 'Loss of shade and natural habitat. Stump may remain if not treated separately.' },
  { img: treeFelling1, title: 'Old Tree Removal', desc: 'Aging trees become structurally weak and pose serious safety risks — timely removal is essential.', adv: 'Eliminates hazard of falling branches, prevents pest infestation, opens space for new growth.', dis: 'Old trees often have deep root systems making removal complex and time-consuming.' },
  { img: treeFelling6, title: 'Aged Tree Clearance', desc: 'Old trees left unattended can become dangerous to people and property nearby.', adv: 'Safer environment, reduced liability risk, allows better light penetration to surrounding plants.', dis: 'Removal of established trees can disturb surrounding soil and nearby plant roots.' },
  { img: treeFelling8, title: 'Unwanted Tree Clearance', desc: 'Trees growing in the wrong place can cause major structural damage — we remove them safely and cleanly.', adv: 'Protects walls, pipes, and driveways from root intrusion. Reclaims usable outdoor space.', dis: 'Can reduce privacy screening and windbreak protection the tree may have been providing.' },
  { img: treeFelling9, title: 'Bush Clearance', desc: 'Overgrown bush and shrub clearance to reclaim your land and restore order to your property.', adv: 'Reduces fire hazard, deters pests and rodents, opens up the property for development or landscaping.', dis: 'Dense bush clearance can disturb wildlife habitats and expose bare soil to erosion.' },
  { img: treeFelling14, title: 'Palm Tree Removal', desc: 'Palms require specialist removal techniques — our team handles them safely from top to base.', adv: 'Eliminates falling frond hazards, prevents root damage to nearby structures, improves sightlines.', dis: 'Palms are tall and heavy, making removal complex — requires skilled climbers and proper equipment.' },
  { img: treeFelling12, title: 'Stump Removal', desc: 'Left-behind stumps are unsightly and dangerous — we grind and remove them completely.', adv: 'Removes tripping hazards, prevents regrowth and pests, leaves the ground ready for replanting or paving.', dis: 'Grinding leaves wood chip debris behind, and deep roots may still require chemical treatment.' },
];

const portfolioImageStyle = (index) => ({
  float: index % 2 === 0 ? 'right' : 'left',
  width: '55%',
  maxWidth: '520px',
  minWidth: '280px',
  height: '320px',
  margin: index % 2 === 0 ? '0 0 1.5rem 1.5rem' : '0 1.5rem 1.5rem 0',
  borderRadius: '16px',
  overflow: 'hidden',
  boxShadow: '0 18px 36px rgba(0,0,0,0.28)',
});

const portfolioImageTagStyle = {
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
};

const testimonials = [
  { name: 'David Johnson', company: 'Residential Client', comment: 'They safely removed a large oak tree that was threatening our roof. Professional and efficient work!', rating: 5 },
  { name: 'Emily Brown', company: 'Property Manager', comment: 'Quick response time for emergency tree removal. Highly skilled and professional team.', rating: 5 },
  { name: 'Michael Chen', company: 'Commercial Property', comment: 'Excellent service from start to finish. They cleaned up everything afterwards.', rating: 5 },
  { name: 'Lisa Anderson', company: 'Residential Client', comment: 'Fair pricing and top-quality work. Will definitely use their services again!', rating: 5 },
];

const TreeFelling = () => {
  return (
    <div style={tfStyles.page}>

      {/* ── Hero ── */}
      <section style={{
        backgroundColor: '#07120b',
        padding: '7rem 1.5rem',
        color: TF.textHeading,
      }}>
        <div style={{ maxWidth: '1200px', margin: '0 auto', overflow: 'hidden' }}>
          <img
            src={treeFelling12}
            alt="Tree felling"
            style={{
              float: 'right',
              width: '42%',
              maxWidth: '520px',
              marginLeft: '2rem',
              marginBottom: '1.5rem',
              borderRadius: '16px',
              boxShadow: '0 24px 48px rgba(0,0,0,0.35)',
              display: 'block',
            }}
          />

          <div style={{ overflow: 'hidden' }}>
            <p style={tfStyles.overline}>Professional Tree Care</p>
            <h1 style={tfStyles.h1}>
              Expert <span style={{ color: TF.orange }}>Tree Felling</span><br />and Property Protection
            </h1>
            <p style={{ color: TF.textBody, maxWidth: '560px', margin: '0 0 2rem', fontFamily: "'Montserrat', sans-serif", lineHeight: 1.7 }}>
              Precision tree removal, stump clearing, and safe cleanup services for homes and businesses.
            </p>
            <button style={tfStyles.btnPrimary}>Request a Free Quote <ArrowRight size={18} /></button>
          </div>
        </div>
      </section>

      {/* ── Services ── */}
      <section style={tfStyles.sectionAlt}>
        <div style={{ maxWidth: '1100px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <p style={tfStyles.overline}>Our Expertise</p>
            <h2 style={tfStyles.h2}>Tree Services We Offer</h2>
            <div style={tfStyles.accentLine}></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1rem' }}>
            {services.map((service, i) => (
              <div key={i} style={tfStyles.card}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                  <Trees size={22} style={{ color: TF.orange, marginTop: '2px', flexShrink: 0 }} />
                  <div>
                    <h4 style={tfStyles.h4}>{service}</h4>
                    <p style={{ margin: 0, color: TF.textMuted, fontSize: '0.85rem' }}>Professional and safe service</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Why Us ── */}
      <section style={{
        ...tfStyles.section,
        backgroundImage: `linear-gradient(to right, rgba(10,26,10,0.95) 50%, rgba(10,26,10,0.7) 100%), url('https://images.unsplash.com/photo-1615729947596-a598e5de0ab3?w=1200&h=600&fit=crop')`,
        backgroundSize: 'cover', backgroundPosition: 'center'
      }}>
        <div style={{ maxWidth: '600px' }}>
          <p style={tfStyles.overline}>Safety First</p>
          <h2 style={tfStyles.h2}>Certified & Insured Professionals</h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem', marginTop: '1.5rem' }}>
            {['Licensed arborists with years of experience','Fully insured for all types of work','State-of-the-art equipment and technology','Emergency response available 24/7','Safe removal of hazardous trees'].map((f, i) => (
              <div key={i} style={{ display: 'flex', gap: '1rem', alignItems: 'flex-start' }}>
                <span style={{ color: TF.orange, fontSize: '1.2rem', flexShrink: 0 }}>✓</span>
                <p style={{ margin: 0, color: TF.textBody, fontFamily: "'Montserrat', sans-serif" }}>{f}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Gallery ── */}
      <section style={tfStyles.sectionAlt}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <p style={tfStyles.overline}>Portfolio</p>
            <h2 style={tfStyles.h2}>Our Previous Projects</h2>
            <div style={tfStyles.accentLine}></div>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '3rem' }}>
            {galleryItems.map((item, i) => (
              <div key={i}>
                <p style={{ ...tfStyles.overline, textAlign: 'center' }}>{item.title}</p>
                <p style={{ textAlign: 'center', color: TF.textBody, fontSize: '0.95rem', marginBottom: '1.25rem', fontFamily: "'Montserrat', sans-serif" }}>{item.desc}</p>
                <div style={{ backgroundColor: TF.bgCardAlt, borderRadius: '12px', padding: '1.5rem', overflow: 'hidden', clear: 'both' }}>
                  <div style={portfolioImageStyle(i)}>
                    <img src={item.img} alt={item.title} style={portfolioImageTagStyle} />
                  </div>
                  <div style={{ color: TF.textBody, marginBottom: '1.25rem', fontFamily: "'Montserrat', sans-serif", lineHeight: 1.75 }}>
                    {item.desc}
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem', marginTop: '0.75rem' }}>
                    <div style={tfStyles.advantageBox}>
                      <p style={{ color: TF.greenLight, fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', margin: '0 0 0.4rem', fontFamily: "'Montserrat', sans-serif" }}>✅ Advantages</p>
                      <p style={{ color: TF.textMuted, fontSize: '0.85rem', margin: 0 }}>{item.adv}</p>
                    </div>
                    <div style={tfStyles.disadvantageBox}>
                      <p style={{ color: TF.textDanger, fontWeight: '600', fontSize: '0.8rem', textTransform: 'uppercase', margin: '0 0 0.4rem', fontFamily: "'Montserrat', sans-serif" }}>⚠️ Disadvantages</p>
                      <p style={{ color: TF.textMuted, fontSize: '0.85rem', margin: 0 }}>{item.dis}</p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Testimonials ── */}
      <section style={tfStyles.section}>
        <div style={{ maxWidth: '900px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '3rem' }}>
            <p style={tfStyles.overline}>Client Reviews</p>
            <h2 style={tfStyles.h2}>What Our Clients Say</h2>
            <div style={tfStyles.accentLine}></div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '1.5rem' }}>
            {testimonials.map((t, i) => (
              <div key={i} style={tfStyles.card}>
                <div style={{ marginBottom: '0.75rem', fontSize: '1.1rem' }}>{'⭐'.repeat(t.rating)}</div>
                <p style={{ fontStyle: 'italic', color: TF.textBody, marginBottom: '1rem', fontFamily: "'Montserrat', sans-serif", lineHeight: 1.6 }}>"{t.comment}"</p>
                <h4 style={{ ...tfStyles.h4, marginBottom: '0.2rem' }}>{t.name}</h4>
                <p style={{ margin: 0, color: TF.orange, fontSize: '0.85rem' }}>{t.company}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section style={{
        backgroundImage: `linear-gradient(rgba(5,15,5,0.82), rgba(5,15,5,0.82)), url('https://images.unsplash.com/photo-1441974231531-c6227db76b6e?w=1200&h=600&fit=crop')`,
        backgroundSize: 'cover', backgroundPosition: 'center',
        padding: '5rem 1.5rem', textAlign: 'center'
      }}>
        <h2 style={tfStyles.h2}>Need Professional Tree Service?</h2>
        <p style={{ color: TF.textBody, marginBottom: '2rem', fontSize: '1.05rem', fontFamily: "'Montserrat', sans-serif" }}>Get a free estimate today. Available 24/7 for emergencies.</p>
        <button style={tfStyles.btnPrimary}>Request Free Estimate <ArrowRight size={18} /></button>
      </section>

      <ContactForm />
    </div>
  );
};

export default TreeFelling;